import { useRelations } from '@entities/relation/model/hooks/use-realtions'
import { useAuth } from '@features/auth-by-telegram/model/hooks/use-auth'
import { AdvertisingBuyPage, AdvertisingBuyPageMobile } from '@pages/advertising-buy-page'
import { AdvertisingSellPage, AdvertisingSellPageMobile } from '@pages/advertising-sell-page'
import { ChannelsPage } from '@pages/channels-page'

import { linkPartner } from '@entities/partner/api'
import { AddPartnerPage, AddPartnerPageMobile } from '@pages/add-partner-page'
import { DraftPostsPage, DraftPostsPageMobile } from '@pages/draft-posts-page'
import { EditPartnerPage, EditPartnerPageMobile } from '@pages/edit-partner-page'
import { LoginPage } from '@pages/login-page'
import { MainPage } from '@pages/main-page'
import { PartnersPage, PartnersPageMobile } from '@pages/partners-page'
import { ProfilePage } from '@pages/profile-page'
import { PublishedPostsPage, PublishedPostsPageMobile } from '@pages/published-page'
import { ReferralProgram } from '@pages/referral-program-page'
import { ScheduledPostsPage, ScheduledPostsPageMobile } from '@pages/scheduled-posts-page'
import { SettingsPage, SettingsPageMobile } from '@pages/settings-page'
import { StatisticsPage } from '@pages/statistics-page/ui/statistics-page'
import { Stock } from '@pages/stock'
import { BuyAdvertising } from '@pages/stock-buy-advertising'
import useMobileView from '@shared/hooks/isMobile'
import { PageTemplate } from '@widgets/page-template'
import { Spin } from 'antd'
import { useCallback, useEffect } from 'react'
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { useLastAppStateStore } from '../model/store/last-app-state-store'
import { RoutePermission } from '../model/types'
import { ProtectedRoute } from './protected-route'

export const AppRouter = () => {
    const { isAuth, isLoading, loginForAnonymous } = useAuth()

    const navigate = useNavigate()
    const location = useLocation()

    const isLoginPage = location.pathname === '/login'

    const {
        actions: { createRelation: createRelationAction }
    } = useRelations()

    const createRelation = useCallback(
        (token: string) => createRelationAction({ tokenUuid: token }).then(() => navigate('/advertising/buy')),
        [createRelationAction, navigate]
    )

    useEffect(() => {
        if (isLoading) return
        const params = new URLSearchParams(location.search)
        const partnerId = params.get('partner_id')
        if (partnerId) {
            linkPartner({ partnerId: +partnerId })
        }
    }, [isLoading, location.search])

    useEffect(() => {
        if (isLoading) return
        const params = new URLSearchParams(location.search)
        const token = params.get('token')
        if (token) {
            if (isAuth) {
                createRelation(token)
            } else {
                loginForAnonymous().then(() => createRelation(token))
            }
        }
    }, [createRelation, isAuth, isLoading, location.search, loginForAnonymous, navigate])

    useEffect(() => {
        const params = new URLSearchParams(location.search)
        const startapp = params.get('startapp')

        if (startapp?.startsWith('load_session')) {
            useLastAppStateStore.persist.rehydrate()
        }
    }, [location.search, navigate])

    useEffect(() => {
        if (isLoading) return
        if (!isAuth) {
            navigate('/login')
        }
    }, [isLoading, isAuth, navigate, isLoginPage])

    const isMobile = useMobileView()

    return (
        <Spin spinning={isLoading} fullscreen={isLoading}>
            <Routes>
                {/* Главная страница */}
                <Route
                    path="/"
                    element={
                        <ProtectedRoute permissions={[RoutePermission.MAIN]}>
                            <PageTemplate />
                        </ProtectedRoute>
                    }
                >
                    <Route index element={<MainPage />} />
                </Route>

                {/* Страница настроек */}
                <Route
                    path="/settings"
                    element={
                        <ProtectedRoute permissions={[RoutePermission.SETTINGS]}>
                            <PageTemplate />
                        </ProtectedRoute>
                    }
                >
                    <Route index element={isMobile ? <SettingsPageMobile /> : <SettingsPage />} />
                </Route>

                {/* Страница профиля */}
                <Route
                    path="/profile"
                    element={
                        <ProtectedRoute permissions={[RoutePermission.PROFILE]}>
                            <PageTemplate />
                        </ProtectedRoute>
                    }
                >
                    <Route index element={<ProfilePage />} />
                </Route>

                {/* Страница каналов */}
                <Route
                    path="/internet-resources"
                    element={
                        <ProtectedRoute permissions={[RoutePermission.CHANNELS]}>
                            <PageTemplate />
                        </ProtectedRoute>
                    }
                >
                    <Route index element={<ChannelsPage />} />
                </Route>

                {/* Страница статистики */}
                <Route
                    path="/statistics"
                    element={
                        <ProtectedRoute permissions={[RoutePermission.STATISTICS]}>
                            <PageTemplate />
                        </ProtectedRoute>
                    }
                >
                    <Route index element={<StatisticsPage />} />
                </Route>

                {/* Блок постов */}
                <Route
                    path="posts"
                    element={
                        <ProtectedRoute permissions={[RoutePermission.POSTING]}>
                            <PageTemplate />
                        </ProtectedRoute>
                    }
                >
                    <Route
                        path="scheduled"
                        element={isMobile ? <ScheduledPostsPageMobile /> : <ScheduledPostsPage />}
                    />
                    <Route path="draft" element={isMobile ? <DraftPostsPageMobile /> : <DraftPostsPage />} />
                    <Route
                        path="published"
                        element={isMobile ? <PublishedPostsPageMobile /> : <PublishedPostsPage />}
                    />
                </Route>

                {/* Реферальная программа */}
                <Route
                    path="referral"
                    element={
                        <ProtectedRoute permissions={[RoutePermission.POSTING]}>
                            <PageTemplate />
                        </ProtectedRoute>
                    }
                >
                    <Route index element={<ReferralProgram />} />
                </Route>

                {/* Страница каналов */}
                <Route
                    path="stock"
                    element={
                        <ProtectedRoute permissions={[RoutePermission.CHANNELS]}>
                            <PageTemplate />
                        </ProtectedRoute>
                    }
                >
                    <Route path="sell-stock" element={<Stock />} />
                    <Route path="buy-advertising" element={<BuyAdvertising />} />
                </Route>

                {/* Блок Рекламы */}
                <Route
                    path="advertising"
                    element={
                        <ProtectedRoute permissions={[RoutePermission.ADVERTISING]}>
                            <PageTemplate />
                        </ProtectedRoute>
                    }
                >
                    <Route path="partners" element={isMobile ? <PartnersPageMobile /> : <PartnersPage />} />
                    <Route path="partners/new" element={isMobile ? <AddPartnerPageMobile /> : <AddPartnerPage />} />
                    <Route path="partners/:id" element={isMobile ? <EditPartnerPageMobile /> : <EditPartnerPage />} />
                    {/* <Route path="tokens" element={<AdTokensPage />} /> */}
                    <Route path="buy" element={isMobile ? <AdvertisingBuyPageMobile /> : <AdvertisingBuyPage />} />
                    <Route path="sell" element={isMobile ? <AdvertisingSellPageMobile /> : <AdvertisingSellPage />} />
                </Route>

                {/* Страница логина */}
                <Route path="/login" element={<LoginPage />} />
            </Routes>
        </Spin>
    )
}
